import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';

interface ConfirmActionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    isDanger?: boolean;
    isLoading?: boolean;
}

export function ConfirmActionModal({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    isDanger = false,
    isLoading = false
}: ConfirmActionModalProps) {
    const { impactOccurred } = useHapticFeedback();

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 pb-20 sm:pb-6">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                    onClick={() => !isLoading && onClose()}
                />
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="relative w-full max-w-sm bg-[#1A1A1A] border border-white/10 rounded-[24px] p-6 shadow-2xl overflow-hidden"
                >
                    {/* Background Glow */}
                    <div className={cn(
                        "absolute top-0 left-1/2 -translate-x-1/2 w-[200px] h-[100px] blur-[60px] opacity-20 pointer-events-none rounded-full",
                        isDanger ? "bg-red-500" : "bg-[#B4F73D]"
                    )} />

                    <div className="relative z-10">
                        <div className="flex justify-center mb-4">
                            <div className={cn(
                                "w-12 h-12 rounded-full flex items-center justify-center border",
                                isDanger
                                    ? "bg-red-500/10 border-red-500/30 text-red-500"
                                    : "bg-[#B4F73D]/10 border-[#B4F73D]/30 text-[#B4F73D]"
                            )}>
                                {isDanger ? <AlertCircle size={28} /> : <CheckCircle2 size={28} />}
                            </div>
                        </div>

                        <h3 className="text-xl font-black text-center text-white mb-2">{title}</h3>
                        <p className="text-gray-400 text-sm text-center mb-8 whitespace-pre-wrap">{message}</p>

                        <div className="flex gap-3 w-full">
                            <button
                                onClick={() => {
                                    impactOccurred('light');
                                    onClose();
                                }}
                                disabled={isLoading}
                                className="flex-1 py-3.5 px-4 rounded-xl font-bold bg-white/5 text-white/70 hover:bg-white/10 transition-colors border border-white/10 disabled:opacity-50"
                            >
                                {cancelText}
                            </button>
                            <button
                                onClick={() => {
                                    impactOccurred('medium');
                                    onConfirm();
                                }}
                                disabled={isLoading}
                                className={`flex-1 py-3 px-4 rounded-xl font-bold text-white transition-all active:scale-95 flex items-center justify-center
                            ${isDanger
                                ? 'bg-red-500 hover:bg-red-400 shadow-[0_0_15px_rgba(239,68,68,0.3)]'
                                : 'bg-tg-button hover:bg-tg-button/90 shadow-[0_0_15px_rgba(59,130,246,0.3)]'
                            }
                        `}    >
                                {isLoading ? (
                                    <div className="w-5 h-5 border-2 border-[#1A1A1A]/30 border-t-[#1A1A1A] rounded-full animate-spin" />
                                ) : confirmText}
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
